import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { AiOutlineClose } from 'react-icons/ai';

import { fetchSelectedAthlete, clearSelectedAthlete } from '../../redux/slices/coachSlice';
import AthleteCard from '../AthleteCard/AthleteCard';
import SearchBar from '../SearchBar/SearchBar';
import styles from './MyAthletesPanel.module.css';
import globalStyles from '../../styles/ButtonGradient.module.css';

const MyAthletesPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { athletes, selectedAthlete } = useSelector((state) => state.coach);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAthletes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return athletes.filter((athlete) =>
      `${athlete.firstName} ${athlete.lastName}`.toLowerCase().includes(term)
    );
  }, [searchTerm, athletes]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleAthleteClick = async (athleteId) => {
    await dispatch(fetchSelectedAthlete(athleteId));
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={styles.panel}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <button className={styles.closeButton} onClick={onClose}>
              <AiOutlineClose size={24} />
            </button>
            <h2 className={styles.title}>My Athletes</h2>
            <div className={styles.searchWrapper}>
              <SearchBar
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {selectedAthlete && (
              <div className={styles.clearViewWrapper}>
                <button
                  className={globalStyles.button}
                  onClick={() => {
                    dispatch(clearSelectedAthlete());
                    onClose();
                  }}
                >
                  Return to Coach View
                </button>
              </div>
            )}

            <div className={styles.listWrapper}>
              {filteredAthletes.length > 0 ? (
                filteredAthletes.map((athlete) => (
                  <AthleteCard
                    key={athlete._id}
                    athlete={athlete}
                    onClick={() => handleAthleteClick(athlete._id)}
                  />
                ))
              ) : (
                <p className={styles.emptyText}>No athletes found.</p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MyAthletesPanel;